import moment from "moment-timezone";

import { type DateInterval } from "../constants";

import { ISO_DATE_FORMAT } from ".";

export class InvalidDateError extends Error {
  constructor(date = "") {
    super(`${date} is an invalid date`);
    Object.setPrototypeOf(this, InvalidDateError.prototype);
  }
}

export function assertISODateFormat(date: string) {
  if (!isISODateFormat(date)) {
    throw new Error(`Date not in YYYY-MM-DD format: ${date}`);
  }
}

export function isISODateFormat(date: string) {
  return parseISODateFormat(date).isValid();
}

export function parseISODateFormat(date: string) {
  return moment(date, ISO_DATE_FORMAT, true);
}

export function shiftISODate(date: string, days: number) {
  assertISODateFormat(date);
  return parseISODateFormat(date).add(days, "days").format(ISO_DATE_FORMAT);
}

export function todayLocalAsISODate() {
  return moment().format(ISO_DATE_FORMAT);
}

export function yesterdayLocalAsISODate() {
  return moment().subtract(1, "day").format(ISO_DATE_FORMAT);
}

export function dateRangeLength(dateRange: DateInterval) {
  const { startDate, endDate } = dateRange;
  return moment(endDate).diff(moment(startDate), "days") + 1;
}

/**
 * @deprecated
 * Should be replaced with inline usage.
 */
export function formatDate(date: string) {
  return moment(date).format("MMM D");
}

/**
 * @deprecated
 * Should be replaced with inline usage.
 */
export function formatDateCompact(date: string) {
  return moment(date).format("M/D");
}

/**
 * @deprecated
 * Should be replaced with inline usage.
 */
export function formatDateFull(date: string) {
  return moment(date).format("MMM D, YYYY");
}

export function orderByDate<T>(dateObjs: T[], accessor: (val: T) => string) {
  const dateObjsDup = [...dateObjs];
  dateObjsDup.sort((d1, d2) => accessor(d1).localeCompare(accessor(d2)));
  return dateObjsDup;
}

export function getBeginningOfMonth(date: string): string {
  const monthStart = moment(date).format("YYYY-MM-[01]");
  if (monthStart === "Invalid date") {
    // https://www.dannyguo.com/blog/how-to-fix-instanceof-not-working-for-custom-errors-in-typescript
    throw new InvalidDateError(date);
  }
  return monthStart;
}

/**
 * @deprecated
 * Should be replaced with inline usage.
 */
export function formatDateTime(date: string, format = "MMM D, YYYY, h:mm a z") {
  return moment(date).format(format);
}

export function convertEndDateToTimeZone(dateString: string, timeZone: string) {
  return moment.tz(dateString, timeZone).endOf("day").toDate();
}

export function convertStartDateToTimeZone(
  dateString: string,
  timeZone: string,
) {
  return moment.tz(dateString, timeZone).startOf("day").toDate();
}
